




































































import { Component, Vue, Watch } from 'vue-property-decorator'
import { ChartData, ChartOptions } from 'chart.js'
import { ReportAPI } from '@/api/client/report'

interface TargetsData {
    in: TargetsDirectionData|null,
    out: TargetsDirectionData|null,
}

interface TargetsDirectionData {
    quantity: number,
    achievedQuantity: number,
    turnover: number,
    achievedTurnover: number,
}

@Component
export default class TargetsBlock extends Vue {
    loading: boolean = false
    data: TargetsData = null
    direction: string = 'in'

    mounted() {
        this.loadData()
    }

    get availableDirections(): string[] {
        const directions = []

        if (this.data && this.data.in !== null) {
            directions.push('in')
        }

        if (this.data && this.data.out !== null) {
            directions.push('out')
        }

        return directions
    }

    get availableDirectionsOptions() {
        return this.availableDirections
            .map(availableDirection => ({
                label: this.$t(`dashboard.targets.direction.${availableDirection}`),
                value: availableDirection,
            }))
    }

    changeDirection(direction: string): void {
        if (!this.availableDirections.includes(direction)) {
            return
        }

        this.direction = direction
    }

    @Watch('availableDirections')
    resetDirection(): void {
        if (!this.availableDirections.includes(this.direction)) {
            this.direction = this.availableDirections[0]
        }
    }

    async loadData(): Promise<void> {
        this.loading = true
        this.data = await ReportAPI.getTargets()
        this.loading = false
    }

    get quantity(): number {
        if (!this.data || !this.data[this.direction]) {
            return 0
        }

        return this.data[this.direction].quantity
    }

    get achievedQuantity(): number {
        if (!this.data || !this.data[this.direction]) {
            return 0
        }

        return this.data[this.direction].achievedQuantity
    }

    formatQuantity(quantity: number) {
        return quantity.toLocaleString('nl', { maximumFractionDigits: 0 })
    }

    get turnover(): number {
        if (!this.data || !this.data[this.direction]) {
            return 0
        }

        return this.data[this.direction].turnover
    }

    get achievedTurnover(): number {
        if (!this.data || !this.data[this.direction]) {
            return 0
        }

        return this.data[this.direction].achievedTurnover
    }

    formatTurnover(turnover: number) {
        return turnover.toLocaleString('nl', { maximumFractionDigits: 0, style: 'currency', currency: 'EUR' })
    }

    chartData(target: number, achieved: number): ChartData {
        return {
            datasets: [
                {
                    data: [
                        achieved,
                        Math.max(0, target - achieved),
                    ],
                    backgroundColor: [
                        'rgb(0, 163, 137)',
                        'rgb(232, 234, 238)',
                    ],
                },
            ],
        }
    }

    get chartOptions(): ChartOptions<'doughnut'> {
        return {
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                },
            },
            circumference: 180,
            rotation: -90,
        }
    }

    chartPlugins(target: number, achieved: number): object[] {
        return [
            this.chartDrawPercentagePlugin(target, achieved),
        ]
    }

    chartDrawPercentagePlugin(target: number, achieved: number): object {
        return {
            afterDraw: chart => {
                // Prepare chart
                const ctx = chart.ctx
                ctx.restore()

                ctx.fillStyle = '#1e2a46'
                ctx.font = '18px \'SofiaPro-Regular\', \'Avenir\', Helvetica, Arial, sans-serif'
                ctx.textAlign = 'center'
                ctx.textBaseline = 'bottom'

                const positionX = chart.width / 2
                const positionY = chart.height

                // Calculate percentage
                const percentage = achieved / target

                // Draw percentage
                ctx.fillText(
                    percentage.toLocaleString('nl', { style: 'percent' }),
                    positionX,
                    positionY - 24,
                )

                // Draw
                ctx.save()
            },
        }
    }
}
